import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useRequestUpdate } from "@/hooks/use-persons";

interface PersonUpdateRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Free-text "request update" from the signed-in user's own profile — for
 * changes they can't self-serve (e.g. their manager). The message is emailed to
 * the configured update-request recipient(s); nothing is applied automatically.
 */
export function PersonUpdateRequestDialog({ open, onOpenChange }: PersonUpdateRequestDialogProps) {
  const request = useRequestUpdate();
  const [message, setMessage] = useState("");

  function handleOpenChange(next: boolean) {
    if (!next) setMessage("");
    onOpenChange(next);
  }

  function submit() {
    const trimmed = message.trim();
    if (!trimmed) return;
    request.mutate(trimmed, { onSuccess: () => handleOpenChange(false) });
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Request an update</DialogTitle>
          <DialogDescription>
            Describe the change you need (for example, a new manager). This is sent to the team that
            maintains employee records — it isn't applied automatically.
          </DialogDescription>
        </DialogHeader>

        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="e.g. My manager should be updated to Jordan Lee."
          className="min-h-[120px]"
          maxLength={2000}
          autoFocus
        />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={submit} disabled={!message.trim() || request.isPending}>
            {request.isPending ? "Sending…" : "Send request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
